import React from 'react';
import styled from 'styled-components';
import SearchBar from './components/SearchBar';
import EmojiGrid from './components/EmojiGrid';
import ThemeNavbar from './components/ThemeNavbar';

const AppContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 2rem;
  font-family: Arial, sans-serif;
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: white;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3), 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  z-index: 100;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  padding: 0.75rem 2rem;
`;

const BrandLogo = styled.div`
  display: flex;
  align-items: center;
  margin-right: auto;
`;

const BrandText = styled.h1`
  font-size: 1.4rem;
  margin: 0;
  font-weight: 600;
  color: #5f6368;
  letter-spacing: -0.5px;
  font-family: 'Poppins', sans-serif;
  
  span {
    color: #4285F4;
    font-weight: 700;
  }
`;

// const SectionTitle = styled.h2`
//   margin-top: 0;
//   margin-bottom: 1rem;
//   color: #202124;
//   font-size: 1.5rem;
//   font-weight: 600;
//   font-family: 'Poppins', sans-serif;
// `;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 5rem;
  padding-top: 2rem;
`;

const ContentContainer = styled.div`
  margin-top: 2rem;
  width: 100%;
  display: flex;
  gap: 20px;
`;

const EmojiListContainer = styled.div`
  flex: 2;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

const EmojiDetailContainer = styled.div`
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 30px;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const EmojiPreview = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  width: 100%;
  
.image-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 5rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  color: rgb(106, 106, 106);
  text-transform: lowercase;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 100%; /* Add this */
  width: 100%; /* Add this */
}

.image-info p {
  margin-top: 1em;
  max-width: 100%; /* Add this */
  text-align: center; /* Add this */
}
`;

const HighResEmoji = styled.img`
  width: 64px;
  height: 64px;
  object-fit: contain;
  margin: 0 auto;
`;


const DownloadButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  background: #4285F4;
  color: white;
  
  &:hover {
    background: #3367d6;
  }
  
  &:disabled {
    background: #f0f0f0;
    color: #5f6368;
    cursor: not-allowed;
  }
`;

const NoSelectionMessage = styled.p`
  color: #5f6368;
  font-family: 'Inter', sans-serif;
  text-align: center;
`;

function App() {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedEmoji, setSelectedEmoji] = React.useState(null);
  const [activeTheme, setActiveTheme] = React.useState('apple');
  const [emojis, setEmojis] = React.useState([]);

  // Fetch emojis and select a random one on initial load
  React.useEffect(() => {
    const fetchAndSelectRandomEmoji = async () => {
      try {
        // Import the emoji service to fetch emojis
        const { fetchEmojisByTheme } = await import('./services/emojiService');
        const emojiData = await fetchEmojisByTheme(activeTheme);
        
        setEmojis(emojiData);
        
        // Select a random emoji from the list
        if (emojiData.length > 0) {
          const randomIndex = Math.floor(Math.random() * emojiData.length);
          setSelectedEmoji(emojiData[randomIndex]);
        }
      } catch (error) {
        console.error("Failed to load initial emoji:", error);
      }
    };

    fetchAndSelectRandomEmoji();
  }, [activeTheme]);

  // Update selected emoji when theme changes
  React.useEffect(() => {
    if (emojis.length > 0) {
      // If we have emojis and the theme changes, select a new random emoji
      const randomIndex = Math.floor(Math.random() * emojis.length);
      setSelectedEmoji(emojis[randomIndex]);
    }
  }, [activeTheme, emojis]);

  const handleEmojiClick = (emoji) => {
    setSelectedEmoji(emoji);
  };

  const handleDownload = () => {
    if (!selectedEmoji) return;
    
    // Generate URL for emoji download using local resources
    const code = selectedEmoji.id.toLowerCase().replace(/-/g, '');
    const theme = selectedEmoji.spriteUrl.includes('apple') ? 'apple' : 
                 selectedEmoji.spriteUrl.includes('google') ? 'google' : 
                 selectedEmoji.spriteUrl.includes('twitter') ? 'twitter' : 'facebook';
    
    const downloadUrl = `/assets/emoji/${theme}/64/${code}.png`;
    
    // Create a temporary link element to trigger the download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${selectedEmoji.name}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Header>
        <HeaderContent>
          <BrandLogo>
            <BrandText>Emoji to PNG Converter</BrandText>
          </BrandLogo>
          <ThemeNavbar activeTheme={activeTheme} onThemeChange={setActiveTheme} />
        </HeaderContent>
      </Header>
      
      <AppContainer>
        <MainContent>
          <SearchBar value={searchTerm} onChange={setSearchTerm} />
          <ContentContainer>
            <EmojiListContainer>
              <EmojiGrid 
                searchTerm={searchTerm} 
                theme={activeTheme}
                onEmojiClick={handleEmojiClick}
                selectedEmoji={selectedEmoji}
              />
            </EmojiListContainer>
            
            <EmojiDetailContainer>
            {/* <SectionTitle>Emoji Preview</SectionTitle> */}

              {selectedEmoji ? (
                <>
                  <EmojiPreview>
                    <HighResEmoji 
                      src={`/assets/emoji/${
                        selectedEmoji.spriteUrl.includes('apple') ? 'apple' : 
                        selectedEmoji.spriteUrl.includes('google') ? 'google' : 
                        selectedEmoji.spriteUrl.includes('twitter') ? 'twitter' : 'facebook'
                      }/64/${selectedEmoji.id.toLowerCase()}.png`}
                      alt={selectedEmoji.name}
                    />
                    <div className='image-info'>
                      <p>{": " + selectedEmoji.name + " :"}</p>
                      <p>{selectedEmoji.id}</p>
                    </div>
                  </EmojiPreview>
                  <DownloadButton onClick={handleDownload}>
                    Download PNG
                  </DownloadButton>
                </>
              ) : (
                <NoSelectionMessage>
                  Select an emoji to view and download
                </NoSelectionMessage>
              )}
            </EmojiDetailContainer>
          </ContentContainer>
        </MainContent>
      </AppContainer>
    </>
  );
}

export default App; 